<template>
  <div ref="intersectionHelper" class="sentinel" />
  <LcTabs
    ref="lctabs"
    v-bind="{ tabs, value }"
    adaptive-height
    :class="{ 'single-tab': tabs.length <= 1 }"
    :container-class="stickyClass"
    tab-flex-direction="row"
    trigger="manual"
    variant="divider"
    render-only-visible
    @update:value="navigateToTab"
  >
    <template v-if="activeTab === HOME_ROUTES.Scratchcards.name" #subnav>
      <ScratchcardPriceFilterBar v-bind="{ activePrices, prices }" @price:click="handleSetFilter" />
    </template>

    <template #tab-panel="{ tab }">
      <component :is="tab.data.comp" :key="tab.name" />
    </template>
  </LcTabs>
</template>

<script lang="ts" setup>
import ForYou from '@/custom-pages/home/children/for-you.vue'
import Keno from '@/custom-pages/home/children/keno.vue'
import Lotteries from '@/custom-pages/home/children/lotteries.vue'
import Scratchcards from '@/custom-pages/home/children/scratchcards.vue'
import { HOME_ROUTES } from '~/constants/homeRoutes'
import { QUERY_PRICES } from '~/constants/queryParams'

type HomeTabData = {
  comp: typeof ForYou | typeof Keno | typeof Lotteries | typeof Scratchcards
  path: string
}

definePageMeta({
  tracking: { name: 'Homepage' },
})

const localePath = useLocalePath()
const route = useRoute()
const routeBaseName = useRouteBaseName()
const { t } = useI18n()

const TAB_BY_ROUTE_NAME: Record<string, string> = Object.fromEntries(
  Object.values(HOME_ROUTES).map((route) => [route.routeName, route.name]),
)

const isScratchcardEnabled = useIsScratchcardEnabled()
const isKenoEnabled = useIsFeatureFlagEnabled('keno')

const { activePrices, prices, setFilter } = useScratchcardFilter({ syncQuery: true })

const { activeTabs: tabs } = useTabs<HomeTabData>(() => [
  {
    data: { comp: ForYou, path: HOME_ROUTES.ForYou.path },
    icon: 'star',
    name: HOME_ROUTES.ForYou.name,
    title: t('home.tab.foryou.title'),
  },
  {
    data: { comp: Lotteries, path: HOME_ROUTES.Lotteries.path },
    icon: 'tab-lotteries',
    name: HOME_ROUTES.Lotteries.name,
    title: t('home.tab.draw.title'),
  },
  {
    active: isScratchcardEnabled,
    data: { comp: Scratchcards, path: HOME_ROUTES.Scratchcards.path },
    icon: 'tab-scratchcards',
    name: HOME_ROUTES.Scratchcards.name,
    title: t('home.tab.scratch.title'),
  },
  {
    active: isKenoEnabled,
    data: { comp: Keno, path: HOME_ROUTES.Keno.path },
    icon: 'keno',
    name: HOME_ROUTES.Keno.name,
    title: t('home.tab.keno.title'),
  },
])

const handleSetFilter = (price: number) => {
  scrollTo({ top: 0, behavior: 'smooth' })
  setFilter(price)
}

const activeTab = computed(
  () => TAB_BY_ROUTE_NAME[String(routeBaseName(route) ?? '')] ?? HOME_ROUTES.ForYou.name,
)

const value = computed(() => activeTab.value)

const navigateToTab = async (name?: string) => {
  if (name === activeTab.value) return

  const nextTab = tabs.value.find((tab) => tab.name === name)
  if (!nextTab) return

  const isScratch = name === HOME_ROUTES.Scratchcards.name
  const query =
    isScratch && activePrices.value.length > 0
      ? { [QUERY_PRICES]: [...activePrices.value] }
      : undefined

  await navigateTo(
    query ? { path: localePath(nextTab.data.path), query } : localePath(nextTab.data.path),
  )
}

const lctabsRef = useTemplateRef('intersectionHelper')
const isStuck = ref(false)
const stickyClass = computed(() => (isStuck.value ? 'stuck-nav' : undefined))
let stickyObserver: IntersectionObserver | null = null

const initStickyObserver = () => {
  const container = lctabsRef.value
  if (!container) return

  stickyObserver = new IntersectionObserver(([entry]) => {
    isStuck.value = !entry.isIntersecting
  })
  stickyObserver.observe(container)
}

onMounted(() => {
  initStickyObserver()
})

onUnmounted(() => {
  stickyObserver?.disconnect()
  stickyObserver = null
})
</script>

<style lang="scss" scoped>
.single-tab {
  margin-top: token('size/space/gap/none');

  /* stylelint-disable-next-line selector-class-pattern */
  &:deep(.lc-tabs__carousel) {
    overflow: visible;
  }

  /* stylelint-disable-next-line selector-class-pattern */
  &:deep(.lc-carousel__container) {
    overflow: visible;
  }
}

/* stylelint-disable-next-line selector-class-pattern */
:deep(.lc-tabs__nav_container) {
  position: sticky;
  top: 0;
  z-index: 10;
  padding: token('size/space/gap/100');
  transition:
    backdrop-filter 0.3s ease,
    box-shadow 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    background:
      linear-gradient(
        0deg,
        token('color/transparent/neutral/40/000') 0%,
        token('color/transparent/neutral/40/000') 100%
      ),
      linear-gradient(
        180deg,
        rgb(token('color/gradient/bg-illustration-prominent-accent-100'), 0.8) 0%,
        rgb(token('color/lightblue/600'), 0.8) 122.11%
      );
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.3s ease;
  }

  @include media-desktop {
    $spacing: calc((100vw - $main-content-width) / 2);

    margin: 0 calc(-1 * $spacing); // fallback
    margin: 0 calc(-0.5 * (100cqw - 100%));
    padding-right: $spacing; // fallback
    padding-right: calc(0.5 * (100cqw - 100%));
    padding-left: $spacing; // fallback
    padding-left: calc(0.5 * (100cqw - 100%));
  }

  nav button {
    padding: token('size/space/gap/150') token('size/space/gap/none');
  }

  /* stylelint-disable-next-line selector-class-pattern */
  .lc-tabs__tablist {
    margin: 0;

    @include media-desktop {
      margin: 0 token('size/space/gap/100');
    }
  }

  /* stylelint-disable-next-line selector-class-pattern */
  &:deep(.lc-carousel__wrap) {
    padding-top: token('size/space/gap/200');
  }
}

.sentinel {
  position: relative;
  top: -5px; // required to match the sticky class behaviour
  height: 0;
}

/* stylelint-disable-next-line selector-class-pattern */
:deep(.stuck-nav) {
  border-bottom: 0.038rem solid token('color/lightblue/300');
  box-shadow:
    0 -0.5rem 1rem 0 rgb(255, 255, 255, 40%) inset,
    0 -0.25rem 0.5rem 0 rgb(255, 255, 255, 40%) inset,
    0 0.25rem 1.5rem 0 rgb(0, 0, 0, 8%);
  backdrop-filter: blur(0.563rem);

  &::before {
    opacity: 1;
  }
}
</style>
